import React, { useEffect, useRef, useState } from "react";
import { useSettings, isOpenNow } from "../../lib/useSettings";
import useUniversePhone from "../../lib/jardins-ole/useUniversePhone";
import { Icon } from "../../lib/icons";
import { ChevronDown } from "lucide-react";
import HoursDropdownContent from "./HoursDropdownContent";

function HoursItem({ s, status, fg }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const todayIdx = (new Date().getDay() + 6) % 7;

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"
        aria-expanded={open}
        data-testid="topbar-hours-toggle"
      >
        <span className={`w-1.5 h-1.5 rounded-full ${status.open ? "bg-emerald-400" : "bg-amber-300"}`} />
        <Icon name={s.contact_icons?.hours || "Clock"} className="w-3 h-3 opacity-80" />
        <span className="truncate">{status.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: fg }} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-80 rounded-2xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden animate-fade-up z-[60] text-[hsl(var(--foreground))]" data-testid="topbar-hours-dropdown">
          <HoursDropdownContent s={s} todayIdx={todayIdx} status={status} />
        </div>
      )}
    </span>
  );
}

const renderItem = (s, it, status, fg, phone) => {
  switch (it.type) {
    case "hours":
      return <HoursItem s={s} status={status} fg={fg} />;
    case "phone":
      return phone ? (
        <a href={`tel:${phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 hover:underline">
          <Icon name={s.cta_icons?.phone || "Phone"} className="w-3 h-3" />
          <span className="tabular-nums">{phone}</span>
        </a>
      ) : null;
    case "email":
      return s.email ? (
        <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 hover:underline">
          <Icon name={s.contact_icons?.email || "Mail"} className="w-3 h-3" />
          <span className="truncate">{s.email}</span>
        </a>
      ) : null;
    case "address":
      return (s.address_street || s.main_city) ? (
        <span className="inline-flex items-center gap-1.5">
          <Icon name={s.contact_icons?.address || "MapPin"} className="w-3 h-3" />
          <span className="truncate">{[s.address_street, s.main_city].filter(Boolean).join(" · ")}</span>
        </span>
      ) : null;
    case "baseline":
      return s.company_baseline ? (
        <span className="uppercase tracking-[0.2em] text-[10px] opacity-80 truncate">{s.company_baseline}</span>
      ) : null;
    case "social": {
      const socials = s.social || {};
      const links = Object.entries(socials).filter(([, v]) => v);
      if (!links.length) return null;
      return (
        <span className="inline-flex items-center gap-2">
          {links.map(([k, v]) => (
            <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100" aria-label={k}>
              <Icon name={k === "facebook" ? "Facebook" : k === "instagram" ? "Instagram" : k === "linkedin" ? "Linkedin" : k === "youtube" ? "Youtube" : "Globe"} className="w-3.5 h-3.5" />
            </a>
          ))}
        </span>
      );
    }
    case "text":
      return it.value ? <span className="truncate">{it.value}</span> : null;
    default:
      return null;
  }
};

export default function Topbar() {
  const { settings } = useSettings();
  const phone = useUniversePhone();
  const s = settings || {};
  if (!s.topbar_enabled) return null;
  const items = Array.isArray(s.topbar_items) ? s.topbar_items.filter(i => i.visible !== false) : [];
  if (items.length === 0) return null;
  const status = isOpenNow(s.hours || []);
  const fg = s.topbar_fg || "#FFFFFF";

  const left = items.filter(i => (i.align || "left") === "left");
  const center = items.filter(i => i.align === "center");
  const right = items.filter(i => i.align === "right");

  return (
    <div
      className="hidden md:flex w-full text-[11px] font-medium"
      style={{ background: s.topbar_bg || "#1E3A5F", color: fg }}
      data-testid="public-topbar"
    >
      <div className="max-w-7xl mx-auto w-full px-6 py-2 flex items-center gap-6">
        <div className="flex items-center gap-5 flex-1 min-w-0">
          {left.map((it, i) => <span key={it.id || i} className="truncate">{renderItem(s, it, status, fg, phone)}</span>)}
        </div>
        <div className="flex items-center gap-5 flex-1 justify-center min-w-0">
          {center.map((it, i) => <span key={it.id || i} className="truncate">{renderItem(s, it, status, fg, phone)}</span>)}
        </div>
        <div className="flex items-center gap-5 flex-1 justify-end min-w-0">
          {right.map((it, i) => <span key={it.id || i} className="truncate">{renderItem(s, it, status, fg, phone)}</span>)}
        </div>
      </div>
    </div>
  );
}
